/**
 * The address on a site or a page, as a link — or null when it is not one.
 *
 * WHY THIS IS CAUTIOUS. `domain` is a LABEL, not a validated URL. The site
 * settings field says so in its own hint: "A label for the handoff, not
 * something Brill publishes to." So it holds whatever she typed —
 * "tevrawood.com", "https://tevrawood.com", "the main one", "" — and only some
 * of that is worth making pressable. A link that goes nowhere is worse than
 * text, because text never promised anything.
 *
 * The test is deliberately dull: no spaces, and a hostname with a dot in it.
 * That passes "tevrawood.com" and "brill.co.uk/pricing", and refuses "my site",
 * "localhost" and "TBC". Anything refused still renders as the plain label it
 * always was.
 *
 * https:// is assumed where no scheme is given, because that is what she means
 * by "tevrawood.com" and because a scheme-less href would resolve against the
 * app's own origin and open Brill inside Brill.
 */
export function siteHref(address) {
  const raw = String(address || "").trim();
  if (!raw || /\s/.test(raw)) return null;

  /* Only http(s). A "javascript:" or "data:" address typed into a handoff
     label has no business becoming a live link on her screen. */
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw) && !/^https?:\/\//i.test(raw)) return null;

  try {
    const url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
    /* A dot, and something either side of it — enough to tell a hostname from
       a note to herself. */
    return /^[^.]+\.[^.]+/.test(url.hostname) ? url.href : null;
  } catch {
    return null;
  }
}
